import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AsaInitialSyncBanner } from "@/components/AsaInitialSyncBanner";
import { MappingGateBanner } from "@/components/MappingGateBanner";
import { MultiEntityPicker, type EntityItem } from "@/components/MultiEntityPicker";
import { DateRangePicker } from "@/components/DateRangePicker";
import {
  rangeFromPreset,
  rangeToApi,
  type DateRange,
  type RangeMode,
  type RangePreset,
} from "@/lib/date-range";
import { useAuth } from "@/lib/useAuth";
import {
  ScalingCard,
  TopPerformingCard,
  UnderPerformingCard,
} from "@/components/CampaignLeaderboards";
import { TopCampaignsCard, type TopSlot } from "@/components/TopCampaignsCard";
import { ByApplicationCard } from "@/components/overview/ByApplicationCard";
import { ByCountryCard } from "@/components/overview/ByCountryCard";
import { ChartCard } from "@/components/overview/ChartCard";
import type { MetricDef } from "@/lib/metrics";
import { ConfigurableKpi } from "@/components/overview/ConfigurableKpi";
import { ConfirmToast } from "@/components/overview/ConfirmToast";
import { OverviewTilesInline } from "@/components/overview/OverviewTilesInline";
import { CopyLinkButton } from "@/components/overview/CopyLinkButton";
import { DeleteViewConfirm } from "@/components/overview/DeleteViewConfirm";
import { LoadError } from "@/components/overview/LoadError";
import { PredictionKpisCard } from "@/components/overview/PredictionKpisCard";
import { RefreshControl } from "@/components/overview/RefreshControl";
import { SaveViewDialog } from "@/components/overview/SaveViewDialog";
import { SkeletonKpis } from "@/components/overview/SkeletonKpis";
import { UnsavedChangesBanner } from "@/components/overview/UnsavedChangesBanner";
import { ViewPicker } from "@/components/overview/ViewPicker";
import { configsEqual } from "@/components/overview/view-config";
import {
  useApps,
  useByApp,
  useByCountry,
  useCampaignsTable,
  useDashboardViews,
  useDefaultApp,
  useDeleteDashboardView,
  useOverview,
  useOverviewEventSeries,
  useRefreshAppsFromAsa,
  useSaveDashboardView,
  useSetDefaultApp,
  useUpdateDashboardView,
} from "@/lib/api-hooks";
import {
  initialsFor,
  parseYmdSafe,
  timeGreeting,
  tintFor,
} from "@/lib/overview-helpers";
import { useLocalStorage } from "@/lib/useLocalStorage";
import type { DashboardView, OverviewViewConfig } from "@/lib/types";

const DEFAULT_KPI_SLOTS = ["spend", "revenue", "roas", "installs", "trials", "cpa"];
const DEFAULT_CHART_METRICS = ["spend", "revenue"];
const DEFAULT_BYAPP_COLS = ["spend", "installs", "trials"];

// parseYmdSafe + other pure helpers live in @/lib/overview-helpers

export function OverviewPage() {
  const { user } = useAuth();
  const appsQuery = useApps();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- URL-backed filter state ------------------------------------------------
  // Dates + app selection + loaded view id live in the URL so links
  // are shareable. The sidebar org switcher still scopes every
  // request via the JWT — org never goes on the URL (we'd leak
  // cross-org access otherwise: if user B opened user A's link
  // while logged into a different org, /api/stats/overview would
  // still scope to B's org and render unrelated numbers under the
  // same apps=/from=/to=). The cross-session contract is: the
  // recipient must be logged into the same org; the URL just
  // restores the dashboard state within that org.
  //
  // Only the INITIAL mount reads the URL (via ref) — after that,
  // local state drives the URL, not the other way around. Keeping
  // the URL reactive would fight user interactions (every setRange
  // would re-run hydration logic). Browser back/forward is a known
  // gap for now; re-entering the page picks up the URL fresh.
  const initialUrlRef = useRef({
    from: searchParams.get("from"),
    to: searchParams.get("to"),
    apps: searchParams.get("apps"),
    view: searchParams.get("view"),
    mode: searchParams.get("mode"),
    preset: searchParams.get("preset"),
  });

  // --- Filters: period, apps --------------------------------------------------
  // Org selection is implicit — whichever org is active in the sidebar
  // switcher scopes every request via the JWT. The old "Account"
  // multi-picker here was always a 1-item list anyway and confused
  // users into thinking they could mix orgs on one screen.
  // Initial state reading order: URL `preset` is the authoritative
  // signal for rolling presets, so we read it first. Without it,
  // a stale URL (with from/to written by an earlier session's auto-
  // sync) would silently demote a rolling "Last 7 days" preset into
  // a frozen `preset=custom` because the only signal would be the
  // dates. Whoever ships a link with `?preset=7d` (or even just
  // visits a page that auto-stamped `?preset=7d` on the previous
  // session) keeps rolling behaviour through reloads.
  const [preset, setPreset] = useState<RangePreset>(() => {
    const u = initialUrlRef.current;
    if (u.preset) return u.preset as RangePreset;
    return u.from && u.to ? "custom" : "7d";
  });
  // Mode resolves before range so we know whether the URL dates
  // should be respected verbatim (static / custom) or recomputed
  // from preset every load (rolling).
  const [mode, setMode] = useState<RangeMode>(() => {
    const u = initialUrlRef.current;
    if (u.mode === "static" || u.mode === "rolling") return u.mode;
    // No explicit mode in URL: rolling is the implicit default UNLESS
    // the page is on a `preset=custom` view (which has no formula to
    // roll forward — exact dates are the only meaningful interp).
    if (u.preset === "custom") return "static";
    return "rolling";
  });
  const [range, setRange] = useState<DateRange>(() => {
    const u = initialUrlRef.current;
    const initialPreset = (u.preset as RangePreset) || (u.from && u.to ? "custom" : "7d");
    const initialMode: RangeMode =
      u.mode === "static" || u.mode === "rolling"
        ? u.mode
        : initialPreset === "custom"
          ? "static"
          : "rolling";
    // For a rolling named preset, ALWAYS recompute relative to today
    // — that's the whole point. URL dates are a snapshot from the
    // last session; ignore them. Static / custom modes respect the
    // exact URL dates.
    const useExact = initialMode === "static" || initialPreset === "custom";
    if (useExact && u.from && u.to) {
      const parsed = parseYmdSafe(u.from, u.to);
      if (parsed) return parsed;
    }
    return rangeFromPreset(initialPreset, new Date());
  });

  const appItems: EntityItem[] = useMemo(
    () =>
      (appsQuery.data ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        // Hide the auto-generated `asa-<adamId>` placeholder — no
        // human should have to read "asa-1571800810" next to an
        // app name. Shows the real bundle id if the collector /
        // admin ever fills one in, nothing otherwise.
        sub: a.bundle_id && !/^asa-\d+$/.test(a.bundle_id) ? a.bundle_id : "",
        tint: tintFor(a.id),
        initials: initialsFor(a.name),
        iconUrl: a.icon_url,
      })),
    [appsQuery.data]
  );
  const [appIds, setAppIds] = useState<(string | number)[]>(() => {
    // Explicit comma-separated list in URL wins — the "all" sentinel
    // and missing case both need the resolved apps list to fill in,
    // so we defer them to the effect below.
    const p = initialUrlRef.current.apps;
    if (p && p !== "all") {
      return p.split(",").map(Number).filter((n) => !Number.isNaN(n));
    }
    return [];
  });
  // Default-app resolution mirrors CampaignsPage (§26). Same hook +
  // same persistence so picking an app on Overview also "sticks" for
  // Ads Manager and vice versa — user explicitly asked for this
  // ("точно такую же логику полностью"). Resolution chain (user
  // sticky → org cache → 12mo fallback → all_apps / no_apps) lives
  // in `app.services.default_app_service`.
  const orgIdForDefault = user?.org_id ?? null;
  const defaultAppQuery = useDefaultApp({
    enabled: true,
    orgId: orgIdForDefault,
  });
  const setDefaultApp = useSetDefaultApp();

  // Once appItems resolves, resolve deferred app cases: "all" →
  // every app; absent URL param → server-resolved default app.
  // Guarded by the existing ref so we don't clobber user-driven
  // selections on later re-renders.
  const appDefaultAppliedRef = useRef(false);
  // Explicit-pick flag: the sticky default-app is persisted ONLY when
  // the user actually changes the picker — NOT when we auto-apply the
  // resolved default. Auto-persisting the default froze it as a sticky,
  // so the "show the top-7d spender by default" rule never re-evaluated
  // (user report 2026-06-03).
  const userPickedAppRef = useRef(false);
  // On org switch, forget the previous org's app selection + URL pick
  // and re-resolve the default for the new org. Without this, the
  // previous org's app stayed selected and the stats query 404'd with
  // "app not available for this organization".
  const prevOrgForResetRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (prevOrgForResetRef.current === undefined) {
      prevOrgForResetRef.current = orgIdForDefault;
      return;
    }
    if (prevOrgForResetRef.current === orgIdForDefault) return;
    prevOrgForResetRef.current = orgIdForDefault;
    initialUrlRef.current.apps = null;
    appDefaultAppliedRef.current = false;
    userPickedAppRef.current = false;
    setAppIds([]);
  }, [orgIdForDefault]);
  useEffect(() => {
    if (appItems.length === 0) return;
    if (appDefaultAppliedRef.current) return;
    const p = initialUrlRef.current.apps;
    if (p === "all") {
      appDefaultAppliedRef.current = true;
      setAppIds(appItems.map((a) => a.id));
      return;
    }
    if (p && p !== "all") {
      // URL carries explicit comma-separated list — already applied
      // by useState initialiser. Mark fired so the persistence
      // effect picks up subsequent changes.
      appDefaultAppliedRef.current = true;
      return;
    }
    // No URL pick. Wait for server-resolved default before picking
    // anything — falling back to "first app in list" the way we used
    // to is misleading because the alphabetical-first app rarely
    // matches the user's actual focus.
    if (!defaultAppQuery.data) return;
    appDefaultAppliedRef.current = true;
    const { app_id, kind } = defaultAppQuery.data;
    if (kind === "no_apps") {
      // No apps in the org — leave selection empty, picker will be
      // disabled by render.
      return;
    }
    if (app_id !== null) {
      setAppIds([app_id]);
    } else {
      // kind = 'all_apps' (or user-sticky-is-all) → roll up portfolio.
      setAppIds(appItems.map((a) => a.id));
    }
  }, [appItems, defaultAppQuery.data]);

  // Persist the user's pick to the server whenever it changes — but
  // only after the initial default was applied, otherwise we'd write
  // the just-resolved default back over itself. Sticky pick is shared
  // with CampaignsPage via `user_ads_manager_app_selection`.
  useEffect(() => {
    // Persist ONLY explicit user picks — never the auto-applied default
    // (see userPickedAppRef). This keeps the resolver's user_sticky
    // layer empty unless the user deliberately chose an app, so the
    // top-7d-spender default stays live instead of freezing.
    if (!userPickedAppRef.current) return;
    if (appItems.length === 0) return;
    if (appIds.length === 0) return;
    const allSelected = appIds.length === appItems.length;
    if (allSelected) {
      setDefaultApp.mutate({ app_id: null, is_all: true });
    } else if (appIds.length === 1) {
      const only = appIds[0];
      if (typeof only === "number") {
        setDefaultApp.mutate({ app_id: only, is_all: false });
      }
    }
    // Multi-app non-all selections aren't persisted — there's no
    // shape for that in `user_ads_manager_app_selection`, and users
    // typically narrow back to single-app for tile_config to apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIds, appItems.length]);

  const filters = useMemo(() => {
    const { date_from, date_to } = rangeToApi(range);
    const allAppsSelected =
      appItems.length > 0 && appIds.length === appItems.length;
    return {
      date_from,
      date_to,
      app_ids: allAppsSelected ? null : (appIds as number[]),
      compare_previous: true,
    };
  }, [range, appIds, appItems.length]);

  // ASA app discovery (§37) — "Synchronize Now" footer in the app
  // picker. The daily worker job covers unattended discovery; this
  // is for users who just added an app in App Store Connect and
  // don't want to wait until 04:00 UTC.
  const syncAppsM = useRefreshAppsFromAsa();
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const onSyncAppsClick = useCallback(() => {
    setSyncStatusMsg(null);
    syncAppsM.mutate(undefined, {
      onSuccess: (data) => {
        const parts: string[] = [];
        if (data.discovered > 0) parts.push(`${data.discovered} new`);
        if (data.refreshed > 0) parts.push(`${data.refreshed} updated`);
        setSyncStatusMsg(
          parts.length
            ? `${parts.join(" · ")} · ${data.total_local} apps`
            : "Already up to date · " + data.total_local + " apps",
        );
      },
      onError: () => {
        setSyncStatusMsg("Sync failed — check Settings → ASA integration");
      },
    });
  }, [syncAppsM]);

  const overviewQuery = useOverview(filters);

  // §36 Phase 5 — synthesise MetricDef[] for the chart from the per-
  // app slot config + per-day series. CPA slots are excluded (the
  // backend skips them in `_overview_tile_series` since daily CPA is
  // too noisy). Re-derived whenever the app changes (since
  // tile_config + tile_series are app-scoped).
  const tileMetrics = useMemo<MetricDef[]>(() => {
    const tcfg = overviewQuery.data?.tile_config;
    const tser = overviewQuery.data?.tile_series;
    if (!tcfg || tcfg.tiles.length === 0 || !tser) return [];
    const byDate = new Map<string, Record<string, number | null>>();
    for (const p of tser) {
      const conv: Record<string, number | null> = {};
      for (const [k, v] of Object.entries(p.slot_values)) {
        conv[k] = v == null ? null : parseFloat(v);
      }
      byDate.set(p.date, conv);
    }
    const palette = [
      "var(--c-accent-600)",
      "#059669",
      "var(--c-warn-500)",
      "var(--c-info-500)",
      "#7c3aed",
      "#e11d48",
    ];
    const titleCase = (s: string) =>
      s
        .replace(/[_.]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const fmtMoney = (v: number | null) =>
      v == null || Number.isNaN(v)
        ? "—"
        : v.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    const fmtInt = (v: number | null) =>
      v == null || Number.isNaN(v)
        ? "—"
        : Math.round(v).toLocaleString("en-US");
    return tcfg.tiles
      .filter((t) => t.metric_kind !== "cpa")
      .map((t): MetricDef => {
        const fullLabel = t.label || titleCase(t.event_type);
        const short =
          fullLabel.length > 14 ? fullLabel.slice(0, 13) + "…" : fullLabel;
        const key = String(t.slot_index);
        return {
          id: `tile:${t.slot_index}`,
          label: fullLabel,
          short,
          unit: t.metric_kind === "revenue" ? "$" : "",
          color:
            palette[t.slot_index % palette.length] ??
            "var(--c-accent-600)",
          goodDir: "up",
          gated: false,
          isRate: false,
          fromKpis: () => null,
          fromSeries: (p) => byDate.get(p.date)?.[key] ?? null,
          fromRow: () => null,
          fromByApp: () => null,
          fmt: t.metric_kind === "revenue" ? fmtMoney : fmtInt,
        };
      });
  }, [overviewQuery.data?.tile_config, overviewQuery.data?.tile_series]);

  // §53 — full per-app event list for the chart's metric picker. Every
  // provider in-app event (top-of-funnel ASA metrics are already in the
  // built-in METRICS list) becomes chartable with count / revenue / CR /
  // ROAS, computed against the chart's own spend+installs series. Only
  // fetched for single-app views.
  const singleAppIdForEvents =
    appIds.length === 1 && Number.isFinite(Number(appIds[0]))
      ? Number(appIds[0])
      : null;
  const eventSeriesQuery = useOverviewEventSeries(
    singleAppIdForEvents,
    filters.date_from,
    filters.date_to,
  );
  const eventChartMetrics = useMemo<MetricDef[]>(() => {
    const data = eventSeriesQuery.data;
    if (!data || data.events.length === 0) return [];
    const byDate = new Map<string, (typeof data.points)[number]>();
    for (const p of data.points) byDate.set(p.date, p);
    const palette = [
      "#059669",
      "var(--c-warn-500)",
      "var(--c-info-500)",
      "#7c3aed",
      "#e11d48",
      "var(--c-accent-600)",
    ];
    const titleCase = (s: string) =>
      s.replace(/[_.]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const trunc = (s: string) => (s.length > 14 ? s.slice(0, 13) + "…" : s);
    const fmtMoney = (v: number | null) =>
      v == null || Number.isNaN(v)
        ? "—"
        : v.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
    const fmtInt = (v: number | null) =>
      v == null || Number.isNaN(v)
        ? "—"
        : Math.round(v).toLocaleString("en-US");
    const fmtPct = (v: number | null) =>
      v == null || Number.isNaN(v) ? "—" : `${v.toFixed(2)}%`;
    const fmtRoas = (v: number | null) =>
      v == null || Number.isNaN(v) ? "—" : `${v.toFixed(2)}×`;
    const out: MetricDef[] = [];
    data.events.forEach((ev, idx) => {
      const base = titleCase(ev.event_type);
      const color = palette[idx % palette.length] ?? "var(--c-accent-600)";
      const common = {
        color,
        gated: false,
        isRate: false,
        fromKpis: () => null,
        fromRow: () => null,
        fromByApp: () => null,
      } as const;
      out.push({
        ...common,
        id: `ev:${ev.key}:count`,
        label: base,
        short: trunc(base),
        unit: "",
        goodDir: "up",
        fromSeries: (p) => byDate.get(p.date)?.counts[ev.key] ?? null,
        fmt: fmtInt,
      });
      out.push({
        ...common,
        id: `ev:${ev.key}:cr`,
        label: `${base} CR`,
        short: trunc(`${base} CR`),
        unit: "%",
        goodDir: "up",
        fromSeries: (p) => {
          const c = byDate.get(p.date)?.counts[ev.key];
          if (c == null || !p.installs) return null;
          return (c / p.installs) * 100;
        },
        fmt: fmtPct,
      });
      if (ev.has_revenue) {
        out.push({
          ...common,
          id: `ev:${ev.key}:rev`,
          label: `${base} Revenue`,
          short: trunc(`${base} Rev`),
          unit: "$",
          goodDir: "up",
          fromSeries: (p) => {
            const r = byDate.get(p.date)?.revenue[ev.key];
            return r == null ? null : parseFloat(r);
          },
          fmt: fmtMoney,
        });
        out.push({
          ...common,
          id: `ev:${ev.key}:roas`,
          label: `${base} ROAS`,
          short: trunc(`${base} ROAS`),
          unit: "×",
          goodDir: "up",
          fromSeries: (p) => {
            const r = byDate.get(p.date)?.revenue[ev.key];
            const sp = parseFloat(p.spend_usd);
            if (r == null || !sp) return null;
            return parseFloat(r) / sp;
          },
          fmt: fmtRoas,
        });
      }
    });
    return out;
  }, [eventSeriesQuery.data]);

  // Merge configured-tile metrics with the full event list for the
  // chart picker, de-duplicated: a provider event that's already a
  // configured tile (count / revenue) shouldn't appear twice. CR + ROAS
  // come only from the event list (tiles don't carry them), so they're
  // always kept. Result per cohort event: count, CR, Revenue, ROAS — each
  // once. (§53)
  const chartExtraMetrics = useMemo<MetricDef[]>(() => {
    const tiles = overviewQuery.data?.tile_config?.tiles ?? [];
    const coveredCount = new Set(
      tiles
        .filter((t) => t.metric_kind === "count" && t.event_source !== "asa")
        .map((t) => `${t.event_source}|${t.event_type}`),
    );
    const coveredRev = new Set(
      tiles
        .filter((t) => t.metric_kind === "revenue")
        .map((t) => `${t.event_source}|${t.event_type}`),
    );
    const fresh = eventChartMetrics.filter((m) => {
      const mm = /^ev:(.+):(count|cr|rev|roas)$/.exec(m.id);
      if (!mm) return true;
      const [, key, kind] = mm;
      if (kind === "count" && coveredCount.has(key)) return false;
      if (kind === "rev" && coveredRev.has(key)) return false;
      return true;
    });
    return [...tileMetrics, ...fresh];
  }, [tileMetrics, eventChartMetrics, overviewQuery.data?.tile_config]);

  // Auto-default chart to first 2 tile slots when the user hasn't
  // touched the picker this session AND chartMetrics is still the
  // system default. Once the user customises, their choice persists.
  useEffect(() => {
    if (userTouchedChartRef.current) return;
    if (tileMetrics.length < 2) return;
    const isSystemDefault =
      chartMetrics.length === DEFAULT_CHART_METRICS.length &&
      chartMetrics.every((m, i) => m === DEFAULT_CHART_METRICS[i]);
    if (!isSystemDefault) return;
    setChartMetrics(tileMetrics.slice(0, 2).map((m) => m.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileMetrics]);

  // Fast per-app rollup — own endpoint, no cohort joins. The app
  // filter is dropped here so the portfolio card always rolls up all
  // apps; narrowing happens in the UI via `app_ids` selection.
  const byAppQuery = useByApp({ ...filters, app_ids: null });
  // Per-country breakdown: only meaningful when scoped to a single
  // app — ASA doesn't attribute spend cross-app cleanly, and mixing
  // countries from multiple apps in one list would be confusing. Hook
  // is gated by `enabled` so no request goes out in the multi-app
  // case.
  const singleAppSelected = appIds.length === 1;
  const byCountryQuery = useByCountry(filters, singleAppSelected);
  // Full campaign list (up to 500) — drives leaderboards + Top
  // campaigns. Respects the app filter so "Performance by campaign"
  // and leaderboards scope to the selected app like every other
  // widget on Overview. Earlier version deliberately dropped
  // app_ids to show the portfolio-wide picture, but that confused
  // users who had explicitly scoped the page to one app (per-app
  // feedback 2026-04-24: selecting Screen Mirroring still surfaced
  // Universal Remote's campaigns here).
  const campaignsQuery = useCampaignsTable(filters, { limit: 500 });
  const campaignRows = campaignsQuery.data?.rows ?? [];
  const appsList = appsQuery.data ?? [];

  const firstName = useMemo(
    () => (user?.full_name || "").split(" ")[0] || "there",
    [user]
  );
  const greeting = useMemo(() => timeGreeting(), []);

  // --- Persistent widget config ----------------------------------------------
  const [kpiSlots, setKpiSlots] = useLocalStorage<string[]>(
    // v2: default grew from 5 to 6 KPI slots (added "cpa"). Existing
    // v1 entries in localStorage would keep 5 otherwise.
    "ov.kpis.v2",
    DEFAULT_KPI_SLOTS
  );
  const setKpiAt = (i: number, id: string) =>
    setKpiSlots((slots) => slots.map((v, j) => (j === i ? id : v)));

  const [chartMetrics, setChartMetrics] = useLocalStorage<string[]>(
    "ov.chart.metrics.v1",
    DEFAULT_CHART_METRICS
  );
  // Track whether the user has touched the chart metric picker this
  // session. If yes, we leave their picks alone even when tile_config
  // proposes per-app defaults — respect explicit choice. If no, an
  // app switch can refresh the chart to that app's first 2 tile slots.
  const userTouchedChartRef = useRef(false);
  const handleChartMetricsChange = useCallback(
    (ids: string[]) => {
      userTouchedChartRef.current = true;
      setChartMetrics(ids);
    },
    [setChartMetrics],
  );
  const [byAppCols, setByAppCols] = useLocalStorage<string[]>(
    "ov.byapp.cols.v1",
    DEFAULT_BYAPP_COLS
  );
  // The right-column card on the first Overview row switches between
  // "Performance by country" (default) and a row of 4 D30-prediction
  // KPI tiles (APPLAYERS experiment). Only meaningful when exactly
  // one app is selected — multi-app view keeps the ByApplication card
  // regardless of this toggle. Persisted per-browser so the user
  // doesn't re-pick on every page load.
  const [byCountryMode, setByCountryMode] = useLocalStorage<
    "country" | "prediction"
  >("ov.bycountry.mode.v1", "country");
  const [lbTop, setLbTop] = useLocalStorage<string>("ov.lb.top.v1", "roas");
  const [lbUnder, setLbUnder] = useLocalStorage<string>("ov.lb.under.v1", "roas");
  const [lbScale, setLbScale] = useLocalStorage<string>("ov.lb.scale.v1", "roas");
  const [scaleBasis, setScaleBasis] = useLocalStorage<"campaigns" | "keywords">(
    "ov.scale.basis.v1",
    "campaigns"
  );
  const [topSlots, setTopSlots] = useLocalStorage<TopSlot[]>("ov.top.slots.v1", [
    { id: "s1", name: "By Spend", metric: "spend" },
    { id: "s2", name: "By ROAS", metric: "roas" },
    { id: "s3", name: "By Installs", metric: "installs" },
  ]);
  const [defaultTop, setDefaultTop] = useLocalStorage<string>(
    "ov.top.default.v1",
    "s1"
  );
  const [activeTop, setActiveTop] = useState<string>(defaultTop);

  // --- Saved dashboard views -------------------------------------------------
  // Scope views to a single app when exactly one is selected; otherwise
  // fall back to "all apps" (NULL app_id on the server). The views
  // list includes shared views from the org too — handy when an admin
  // has prepared a common dashboard.
  const viewScopeAppId = appIds.length === 1 ? (appIds[0] as number) : null;
  const qc = useQueryClient();
  const viewsQuery = useDashboardViews(viewScopeAppId);
  const views = viewsQuery.data ?? [];
  const saveView = useSaveDashboardView();
  const updateView = useUpdateDashboardView();
  const deleteView = useDeleteDashboardView();

  // A view is "loaded" when its config has been applied to the local
  // widget state. We keep the view's id as the baseline reference to
  // compare against — any diff in the current state flips the
  // unsaved-changes banner on.
  const [loadedViewId, setLoadedViewId] = useState<number | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Deletion confirmation: null = hidden, populated = modal shown.
  // Replaces the old browser `confirm()` call — native confirm is
  // ugly, un-styleable, and shows a URL in some browsers.
  const [viewPendingDelete, setViewPendingDelete] = useState<DashboardView | null>(null);

  // Transient confirmation toast — shown for ~2.5s after a manual
  // refresh. Cleared automatically so we don't pile up timers; the
  // component renders via a portal so it floats above everything.
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), 2500);
    return () => clearTimeout(t);
  }, [toastMsg]);

  const currentConfig: OverviewViewConfig = useMemo(
    () => ({
      kpiSlots,
      chartMetrics,
      byAppCols,
      lbTop,
      lbUnder,
      lbScale,
      scaleBasis,
      topSlots,
      defaultTop,
      // Period/date range saved so reopening the view restores the
      // same calendar scope. With mode="rolling" (default) the saved
      // preset re-applies as a rolling window at load time. With
      // mode="static" the saved dateFrom / dateTo are reused verbatim
      // regardless of preset — useful for archival snapshots.
      preset,
      dateFrom: rangeToApi(range).date_from,
      dateTo: rangeToApi(range).date_to,
      mode,
    }),
    [
      kpiSlots,
      chartMetrics,
      byAppCols,
      lbTop,
      lbUnder,
      lbScale,
      scaleBasis,
      topSlots,
      defaultTop,
      preset,
      range,
      mode,
    ]
  );

  // Stable identity via useCallback so the auto-load effect below can
  // legitimately list `applyView` in its deps array without re-firing
  // every render. Body uses only useState setters (stable refs) and
  // pure imports — no render-state captured, so deps are [].
  const applyView = useCallback(
    (v: DashboardView, opts?: { skipDates?: boolean }) => {
      const c = v.config ?? {};
      if (c.kpiSlots) setKpiSlots(c.kpiSlots);
      if (c.chartMetrics) setChartMetrics(c.chartMetrics);
      if (c.byAppCols) setByAppCols(c.byAppCols);
      if (c.lbTop) setLbTop(c.lbTop);
      if (c.lbUnder) setLbUnder(c.lbUnder);
      if (c.lbScale) setLbScale(c.lbScale);
      if (c.scaleBasis) setScaleBasis(c.scaleBasis);
      if (c.topSlots) setTopSlots(c.topSlots);
      if (c.defaultTop) setDefaultTop(c.defaultTop);
      // Restore period/date range. For preset values ("7d", "30d",…)
      // we re-derive the rolling window from today so the view stays
      // "last 7 days" in meaning, not a frozen calendar. "custom"
      // locks the exact dates the view was saved with.
      //
      // `skipDates` is set when the caller has an authoritative date
      // source outside the view — e.g. the URL's `from`/`to` params
      // on a shared-link hydration. Without this guard, opening
      // /overview?view=X&from=...&to=... would snap back to the
      // view's saved window and silently discard the link's dates.
      if (!opts?.skipDates && c.preset) {
        const savedMode: RangeMode =
          c.mode === "static" ? "static" : "rolling";
        setPreset(c.preset as RangePreset);
        setMode(savedMode);
        // mode='static' (or preset='custom') → use the exact dates
        // saved with the view. mode='rolling' with a named preset →
        // recompute from today so the window stays meaningful.
        const useExact =
          savedMode === "static" || c.preset === "custom";
        if (useExact && c.dateFrom && c.dateTo) {
          const parsed = parseYmdSafe(c.dateFrom, c.dateTo);
          if (parsed) setRange(parsed);
        } else {
          setRange(rangeFromPreset(c.preset as RangePreset, new Date()));
        }
      }
      setLoadedViewId(v.id);
      setBannerDismissed(false);
    },
    // All setters are stable refs (useState / useLocalStorage); listing
    // them satisfies exhaustive-deps without recreating the callback.
    [
      setKpiSlots,
      setChartMetrics,
      setByAppCols,
      setLbTop,
      setLbUnder,
      setLbScale,
      setScaleBasis,
      setTopSlots,
      setDefaultTop,
    ]
  );

  // Auto-select the user's private default view (or the org's shared
  // default as fallback) whenever the scope settles into a new
  // (app-id, user-id) tuple. We DON'T want to fire on the first
  // render before `user` has resolved from the auth context — the
  // private-default filter would silently fail the owner check and
  // fall through to the shared default (or to nothing), so the user
  // sees the built-in system defaults instead of their pinned view.
  //
  // Using a per-scope ref means changing the app filter re-attempts
  // the auto-load for the new scope, without fighting a user who has
  // already manually picked something inside the same scope.
  const autoLoadedScopeRef = useRef<string | null>(null);
  // If the URL carries `?view=123`, prioritise that view over the
  // user's personal default — recipient of a shared link should
  // land exactly on the sender's saved dashboard, not bounce to
  // their own private default. Only honoured on the first mount
  // (ref-guarded) so the toggle still works normally after.
  const urlViewAppliedRef = useRef(false);
  useEffect(() => {
    if (!viewsQuery.data) return;
    if (!user?.id) return; // wait for auth to resolve
    const scopeKey = `${user.id}:${viewScopeAppId ?? "null"}`;

    // One-shot URL view apply — runs ahead of per-scope auto-load
    // and marks the scope as "handled" so we don't double-apply.
    const urlViewIdRaw = initialUrlRef.current.view;
    if (urlViewIdRaw && !urlViewAppliedRef.current) {
      urlViewAppliedRef.current = true;
      const urlViewId = Number(urlViewIdRaw);
      if (!Number.isNaN(urlViewId)) {
        const v = viewsQuery.data.find((x) => x.id === urlViewId);
        if (v) {
          autoLoadedScopeRef.current = scopeKey;
          // URL dates win over the view's saved range when both
          // are present — the sender's link always renders the
          // exact window they had open, even if the view was
          // saved with a different preset.
          const hasUrlDates =
            !!initialUrlRef.current.from && !!initialUrlRef.current.to;
          applyView(v, { skipDates: hasUrlDates });
          return;
        }
      }
    }

    if (autoLoadedScopeRef.current === scopeKey) return;
    autoLoadedScopeRef.current = scopeKey;
    if (viewsQuery.data.length === 0) return;
    const privateDefault = viewsQuery.data.find(
      (v) =>
        v.visibility === "private" &&
        v.is_default &&
        v.owner_user_id === user.id,
    );
    const sharedDefault = viewsQuery.data.find(
      (v) => v.visibility === "shared" && v.is_default,
    );
    const pick = privateDefault ?? sharedDefault ?? null;
    if (pick) applyView(pick);
  }, [viewsQuery.data, user?.id, viewScopeAppId, applyView]);

  // --- Sync state → URL -------------------------------------------------------
  // Write the current filter state back to the address bar so the
  // URL is always a paste-ready snapshot. `replace: true` keeps
  // history clean — otherwise every date-preset click would stack
  // a new history entry, breaking Back.
  //
  // Dates go in as absolute yyyy-mm-dd — presets are relative, so
  // "7d" today differs from "7d" tomorrow; shared links must pin
  // the window. Apps: explicit comma-separated list, plus an "all"
  // sentinel when every app is selected (so the URL stays short
  // for portfolio views). `view` carries the loaded saved view.
  useEffect(() => {
    // Guard: wait until apps have settled on first mount so we
    // don't write a transient empty apps param to the URL.
    if (appItems.length > 0 && appIds.length === 0) return;

    const next = new URLSearchParams(searchParams);
    const { date_from, date_to } = rangeToApi(range);
    next.set("from", date_from);
    next.set("to", date_to);

    // Persist `preset` so the rolling-vs-static interpretation
    // survives a reload. Without this the URL ends up with just
    // `from` and `to`, and the page can't tell whether the user
    // originally picked "Last 7 days" (rolling) or hand-picked
    // those exact dates (custom) — defaulting to custom silently
    // freezes rolling presets, which the user spotted on Overview.
    next.set("preset", preset);

    // mode=rolling is the implicit default — only stamp the URL when
    // the user has explicitly switched to "static" so shared links
    // stay short for the common case.
    if (mode === "static") next.set("mode", "static");
    else next.delete("mode");

    if (appItems.length === 0 || appIds.length === 0) {
      next.delete("apps");
    } else if (appIds.length === appItems.length) {
      next.set("apps", "all");
    } else {
      next.set("apps", appIds.join(","));
    }

    if (loadedViewId != null) next.set("view", String(loadedViewId));
    else next.delete("view");

    // Skip the write when nothing would change — setSearchParams
    // can otherwise nudge React Router into a no-op render loop
    // on every state tick.
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [range, mode, preset, appIds, appItems.length, loadedViewId, searchParams, setSearchParams]);

  const loadedView = loadedViewId
    ? views.find((v) => v.id === loadedViewId) ?? null
    : null;

  // Baseline = whatever the dashboard was showing before the user
  // started tweaking. If they've loaded a saved view, that's the
  // baseline; otherwise it's the built-in system default layout.
  // Either way, changing anything away from the baseline raises the
  // "unsaved changes" banner so the user is prompted to save.
  const systemDefaultConfig: OverviewViewConfig = useMemo(
    () => ({
      kpiSlots: DEFAULT_KPI_SLOTS,
      chartMetrics: DEFAULT_CHART_METRICS,
      byAppCols: DEFAULT_BYAPP_COLS,
      lbTop: "roas",
      lbUnder: "roas",
      lbScale: "roas",
      scaleBasis: "campaigns",
      topSlots: [
        { id: "s1", name: "By Spend", metric: "spend" },
        { id: "s2", name: "By ROAS", metric: "roas" },
        { id: "s3", name: "By Installs", metric: "installs" },
      ],
      defaultTop: "s1",
    }),
    []
  );
  const baselineConfig = loadedView?.config ?? systemDefaultConfig;

  const isDirty = useMemo(
    () => !configsEqual(currentConfig, baselineConfig),
    [currentConfig, baselineConfig]
  );

  const showBanner = isDirty && !bannerDismissed;

  return (
    <div className="content">
      {toastMsg && <ConfirmToast message={toastMsg} />}
      {/* Page header: title on the left, filters on the right. App
          picker + date range sit together in the top-right so the
          user can scope the dashboard without scrolling or hunting
          through a second row of controls. */}
      {/* Outer row never wraps — greeting shrinks, filters stay pinned
          on the right. flexWrap: "nowrap" ensures the pickers sit in
          the header row even on narrower viewports. */}
      <div
        className="page-head"
        style={{
          alignItems: "flex-start",
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "nowrap",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            minWidth: 0,
            flex: "1 1 auto",
            overflow: "hidden",
          }}
        >
          <h1
            className="page-title"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {greeting}, <em>{firstName}</em>
          </h1>
          <div className="page-sub">
            Here&apos;s how your apps performed across Apple Search Ads over
            the selected period.
          </div>
        </div>
        <div
          className="row ov-filter-row"
          style={{
            // stretch + fixed 52px row guarantees every picker in the
            // header sits on identical top/bottom baselines. The
            // `.ov-filter-row` class also pixel-locks border-radius
            // and box-shadow across .app-picker / .range-picker /
            // .refresh-ctl so they read as one visual family — the
            // previous mix (different radius + shadow on only some)
            // made the four drift visually even though heights
            // matched.
            alignItems: "stretch",
            height: 52,
            gap: 10,
            flex: "0 0 auto",
            flexWrap: "nowrap",
            justifyContent: "flex-end",
          }}
        >
          {views.length > 0 && (
            <ViewPicker
              views={views}
              activeId={loadedViewId}
              currentUserId={user?.id ?? null}
              isAdmin={user?.role === "admin"}
              onPick={(v) => applyView(v)}
              onToggleDefault={(v) =>
                void updateView.mutateAsync({
                  id: v.id,
                  is_default: !v.is_default,
                })
              }
              onDelete={(v) => setViewPendingDelete(v)}
            />
          )}
          <MultiEntityPicker
            items={appItems}
            values={appIds}
            onChange={(v) => {
              userPickedAppRef.current = true;
              setAppIds(v);
            }}
            label="App"
            width={240}
            pluralLabel="apps"
            allLabel="All apps"
            syncNow={{
              onClick: onSyncAppsClick,
              loading: syncAppsM.isPending,
              status: syncStatusMsg,
            }}
          />
          <DateRangePicker
            preset={preset}
            range={range}
            mode={mode}
            onChange={({ preset, range, mode }) => {
              setPreset(preset);
              setRange(range);
              setMode(mode);
            }}
          />
          <CopyLinkButton onCopied={() => setToastMsg("Link copied")} />
          <RefreshControl
            lastUpdatedAt={overviewQuery.dataUpdatedAt}
            busy={overviewQuery.isFetching || byAppQuery.isFetching}
            onRefresh={async () => {
              await Promise.all([
                overviewQuery.refetch(),
                byAppQuery.refetch(),
                singleAppSelected
                  ? byCountryQuery.refetch()
                  : Promise.resolve(),
                campaignsQuery.refetch(),
              ]);
              setToastMsg("Dashboard updated");
            }}
          />
        </div>
      </div>

      {showBanner && (
        <UnsavedChangesBanner
          viewName={loadedView?.name ?? null}
          onDismiss={() => setBannerDismissed(true)}
          onSave={() => setSaveDialogOpen(true)}
        />
      )}

      {saveDialogOpen && (
        <SaveViewDialog
          isAdmin={user?.role === "admin"}
          defaultName={loadedView?.name ?? ""}
          onCancel={() => setSaveDialogOpen(false)}
          onSubmit={async ({ name, visibility, setDefault }) => {
            const created = await saveView.mutateAsync({
              name,
              app_id: viewScopeAppId,
              config: currentConfig,
              visibility,
              set_default: setDefault,
            });
            // Seed the query cache with the returned view so
            // `views.find(v => v.id === loadedViewId)` resolves
            // synchronously on the next render. Without this the
            // background refetch from invalidateQueries has a small
            // window where `loadedView` is still null, baseline
            // falls back to system default, and the banner flashes
            // even though the save succeeded.
            qc.setQueryData<DashboardView[] | undefined>(
              ["dashboard-views", viewScopeAppId],
              (old) => {
                if (!old) return [created];
                // Replace if present, otherwise append.
                const idx = old.findIndex((v) => v.id === created.id);
                if (idx >= 0) {
                  const next = [...old];
                  next[idx] = created;
                  return next;
                }
                return [...old, created];
              }
            );
            setLoadedViewId(created.id);
            setBannerDismissed(false);
            setSaveDialogOpen(false);
          }}
        />
      )}

      {viewPendingDelete && (
        <DeleteViewConfirm
          view={viewPendingDelete}
          onCancel={() => setViewPendingDelete(null)}
          onConfirm={async () => {
            const id = viewPendingDelete.id;
            try {
              await deleteView.mutateAsync(id);
              if (loadedViewId === id) setLoadedViewId(null);
              setToastMsg(`View "${viewPendingDelete.name}" deleted`);
            } finally {
              setViewPendingDelete(null);
            }
          }}
        />
      )}

      {overviewQuery.isLoading && <SkeletonKpis count={kpiSlots.length} />}
      {overviewQuery.error && (
        <LoadError onRetry={() => void overviewQuery.refetch()} />
      )}

      {overviewQuery.data && (
        <>
          <AsaInitialSyncBanner status={overviewQuery.data.sync_status} />
          <MappingGateBanner gate={overviewQuery.data.gate} />

          {/* KPI grid (§36 inline editor):
              - Single-app view WITH per-app tiles configured
                (`tile_config.tiles` non-empty) → the inline per-app tile
                editor: Spend + Installs pinned + read-only, the rest
                editable in place (hover pencil to pick the event/metric,
                hover trash to remove, empty "+" to add, one Save bar).
              - Everything else (multi-app / no-app, OR a single app that
                has NO per-app tile config) → the legacy fixed kpiSlots
                grid. This fallback is what a loaded dashboard's saved
                `config.kpiSlots` renders through; without it, single-app
                views on apps that never configured per-app event tiles
                showed only the 2 pinned tiles and the user's saved
                dashboard tile picks were ignored. Per-app event tiles are
                still configurable for any app via Settings → App Settings
                → Overview tiles, and this branch flips to the inline
                editor the moment that app has ≥1 configured slot. */}
          {(() => {
            const tcfg = overviewQuery.data.tile_config;
            const kpisData = overviewQuery.data.kpis;
            const comparisonData = overviewQuery.data.comparison;
            const seriesData = overviewQuery.data.time_series;

            const onlyAppId =
              appIds.length === 1 ? Number(appIds[0]) : null;
            if (
              tcfg &&
              tcfg.tiles.length > 0 &&
              onlyAppId != null &&
              Number.isFinite(onlyAppId)
            ) {
              return (
                <OverviewTilesInline
                  appId={onlyAppId}
                  resolvedTiles={tcfg.tiles}
                  kpis={kpisData}
                  comparison={comparisonData}
                  series={seriesData}
                />
              );
            }

            // Multi-app / no-app OR single-app with no per-app tile
            // config → legacy fixed kpiSlots grid (saved-dashboard tiles).
            return (
              <div className="kpi-grid">
                {kpiSlots.map((id, i) => (
                  <ConfigurableKpi
                    key={i}
                    metricId={id}
                    // Duplicates allowed — the admin chooses the layout,
                    // even if that means showing the same metric twice.
                    exclude={[]}
                    onChange={(mid) => setKpiAt(i, mid)}
                    kpis={kpisData}
                    comparison={comparisonData}
                    series={seriesData}
                  />
                ))}
              </div>
            );
          })()}

          <div style={{ height: 16 }} />

          {/* Per-app tile chart support (§36 Phase 5). When
              `tile_config` carries non-CPA slots and `tile_series`
              landed alongside, surface them as `extraMetrics` so the
              chart picker can pick them, and auto-default the chart
              to the first 2 slots when the user hasn't customised
              their picks. CPA slots are excluded from the chart
              upstream (too noisy per-day). */}
          <div className="grid-2">
            <ChartCard
              series={overviewQuery.data.time_series}
              metrics={chartMetrics}
              onMetricsChange={handleChartMetricsChange}
              extraMetrics={chartExtraMetrics}
            />
            {singleAppSelected ? (
              byCountryMode === "prediction" ? (
                <PredictionKpisCard
                  kpis={overviewQuery.data.kpis}
                  mode={byCountryMode}
                  onModeChange={setByCountryMode}
                />
              ) : (
                <ByCountryCard
                  rows={byCountryQuery.data?.rows ?? []}
                  loading={byCountryQuery.isLoading}
                  metricIds={byAppCols}
                  onMetricsChange={setByAppCols}
                  mode={byCountryMode}
                  onModeChange={setByCountryMode}
                />
              )
            ) : (
              <ByApplicationCard
                apps={appsQuery.data ?? []}
                byApp={byAppQuery.data?.rows ?? []}
                loading={byAppQuery.isLoading}
                metricIds={byAppCols}
                onMetricsChange={setByAppCols}
              />
            )}
          </div>

          <div style={{ height: 16 }} />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 2px 10px",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                Campaign leaderboard
              </h2>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                Best &amp; worst performers and scaling opportunities for the
                selected period
              </div>
            </div>
          </div>

          <div className="lb-grid">
            <TopPerformingCard
              rows={campaignRows}
              apps={appsList}
              metricId={lbTop}
              onMetricChange={setLbTop}
              loading={campaignsQuery.isLoading}
            />
            <UnderPerformingCard
              rows={campaignRows}
              apps={appsList}
              metricId={lbUnder}
              onMetricChange={setLbUnder}
              loading={campaignsQuery.isLoading}
            />
            <ScalingCard
              rows={campaignRows}
              apps={appsList}
              metricId={lbScale}
              onMetricChange={setLbScale}
              basis={scaleBasis}
              onBasisChange={setScaleBasis}
              loading={campaignsQuery.isLoading}
            />
          </div>

          <div style={{ height: 16 }} />

          <TopCampaignsCard
            rows={campaignRows}
            apps={appsList}
            slots={topSlots}
            onSlotsChange={setTopSlots}
            activeSlot={activeTop}
            onActiveSlot={setActiveTop}
            defaultSlot={defaultTop}
            onDefaultSlot={setDefaultTop}
            loading={campaignsQuery.isLoading}
          />
        </>
      )}
    </div>
  );
}


